import { ButtonSize } from "@galaxy-io/dls/buttons/Button";
import DescriptionList from "@galaxy-io/dls/lists/DescriptionList";
import Tabs from "@galaxy-io/dls/navigation/Tabs";
import Span from "@galaxy-io/dls/text/Span";
import { FontFamily } from "@galaxy-io/dls/theme/enums";
import {
  BuildingsIcon,
  ClockCounterClockwiseIcon,
  PencilSimpleIcon,
  ProhibitIcon,
  TrashIcon,
} from "@phosphor-icons/react";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useUrlState } from "@/app/navigation";
import { QueryState } from "@/components/query-state";
import {
  DrawerExternalLink,
  DrawerList,
  DrawerSection,
  DrawerStack,
} from "@/components/workspace/drawer-content";
import { MenuItem, OverflowMenu } from "@/components/workspace/overflow-menu";
import { RecordAudit } from "@/components/workspace/record-audit";
import { RecordQueries } from "@/components/workspace/record-queries";
import { SurfaceDialog } from "@/components/workspace/surface-dialog";
import { useRetainedValue } from "@/components/workspace/use-retained-value";
import { CompanyPeople } from "@/features/companies/company-people";
import { ExclusionActions, ExclusionStatus } from "@/features/exclusions/exclusion-action";
import { DeleteEntity } from "@/features/people/delete-entity";
import { EntityAssignment } from "@/features/people/entity-assignment";
import { EntityTags } from "@/features/tags/tag-controls";
import { apiClient, unwrap } from "@/lib/api-client";
import { companyQueries } from "@/lib/record-queries";
import { date, safeUrl } from "@/lib/utils";

export function CompanyInspector() {
  const { params, update } = useUrlState();
  const selectedId = params.get("companyId");
  const open = !!selectedId && selectedId !== "new";
  const id = useRetainedValue(open ? selectedId : null) ?? "";
  const [deleteOpen, setDeleteOpen] = useState(false);
  const result = useQuery({
    queryKey: ["company", id],
    enabled: open,
    queryFn: ({ signal }) =>
      unwrap(
        apiClient().GET("/companies/{id}", {
          params: { path: { id: id! } },
          signal,
        }),
      ),
    retry: false,
  });
  const company = result.data;
  const selectedTab = params.get("companyTab");
  const tab =
    selectedTab === "people" || selectedTab === "audit" || selectedTab === "queries"
      ? selectedTab
      : "overview";
  return (
    <SurfaceDialog
      open={open}
      drawer
      flush
      title="Company"
      headerTitle={company?.name || "Company"}
      headerActions={
        company && (
          <ExclusionActions
            kind="company"
            id={id}
            trigger={(open, excluded) => (
              <OverflowMenu size={ButtonSize.SMALL} label="More actions">
                {(close) => (
                  <>
                    <MenuItem
                      icon={PencilSimpleIcon}
                      label="Edit"
                      onClick={() => {
                        close();
                        update({ companyEdit: "1" });
                      }}
                    />
                    <MenuItem
                      icon={ProhibitIcon}
                      label={excluded ? "Excluded" : "Exclude…"}
                      onClick={() => {
                        close();
                        open();
                      }}
                    />
                    <MenuItem
                      icon={TrashIcon}
                      label="Delete company"
                      onClick={() => {
                        close();
                        setDeleteOpen(true);
                      }}
                      isDestructive
                    />
                  </>
                )}
              </OverflowMenu>
            )}
          />
        )
      }
      headerIcon={BuildingsIcon}
      onClose={() => update({ companyId: null, companyTab: null, companyPage: null })}
      restoreFocus={() =>
        document.querySelector<HTMLAnchorElement>(`a[data-company-id="${CSS.escape(id)}"]`)?.focus()
      }
    >
      {company && (
        <DeleteEntity
          open={deleteOpen}
          onClose={() => setDeleteOpen(false)}
          kind="company"
          id={id}
          name={company.name}
          onDeleted={() =>
            update({ companyId: null, companyTab: null, companyPage: null, companyEdit: null })
          }
        />
      )}
      {result.error ? (
        <QueryState error={result.error} onRetry={() => void result.refetch()} />
      ) : !company ? (
        <QueryState loadingMessage="Loading company…" />
      ) : (
        <>
          <Tabs
            ariaLabel="Company sections"
            value={tab}
            onChange={(value) => update({ companyTab: value === "overview" ? null : value })}
            items={[
              { id: "overview", label: "Overview" },
              { id: "people", label: "People", count: company.peopleCount },
              { id: "queries", label: "Queries" },
              { id: "audit", label: "Audit" },
            ]}
          />
          {tab === "people" ? (
            <CompanyPeople
              companyId={id}
              page={params.get("companyPage") || undefined}
              onPageChange={(page) => update({ companyPage: page > 1 ? String(page) : null })}
            />
          ) : tab === "queries" ? (
            <DrawerStack>
              <RecordQueries queries={companyQueries(id)} />
            </DrawerStack>
          ) : tab === "audit" ? (
            <DrawerStack>
              <RecordAudit kind="company" id={id} />
              <DrawerSection header="Record details" icon={ClockCounterClockwiseIcon}>
                <DescriptionList
                  items={[
                    { label: "Company ID", value: company.id },
                    { label: "Workspace ID", value: company.tenantId },
                  ]}
                />
                {company.deletedAt && (
                  <DescriptionList
                    items={[
                      {
                        label: "Deleted",
                        value: date(company.deletedAt, true),
                        family: FontFamily.MONO,
                      },
                      { label: "Deleted by", value: company.deletedBy || "—" },
                    ]}
                  />
                )}
              </DrawerSection>
            </DrawerStack>
          ) : (
            <DrawerStack>
              <DrawerList>
                <DescriptionList
                  items={[
                    { label: "Tags", value: <EntityTags kind="company" id={id} asValue /> },
                    {
                      label: "Assigned to",
                      value: (
                        <EntityAssignment
                          kind="company"
                          id={id}
                          assignedTo={company.assignedTo}
                          asValue
                        />
                      ),
                    },
                    { label: "Name", value: company.name },
                    { label: "Domain", value: company.domain || "—" },
                    {
                      label: "Website",
                      value: safeUrl(company.url || "") ? (
                        <DrawerExternalLink
                          href={safeUrl(company.url!)!}
                          target="_blank"
                          rel="noreferrer"
                          title={company.url || undefined}
                        >
                          <Span>{company.url}</Span>
                        </DrawerExternalLink>
                      ) : (
                        "—"
                      ),
                    },
                    { label: "People", value: company.peopleCount },
                  ]}
                />
              </DrawerList>
              <ExclusionStatus kind="company" id={id} />
            </DrawerStack>
          )}
        </>
      )}
    </SurfaceDialog>
  );
}
